import { type AttributeValue, attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';

/** The same three steps a `Button` takes, with the same default, because a form
 * must never put a control of one height beside a control of another. Both
 * resolve to the shared `--bomdisoft-control-*` scale, so a surface moves its
 * buttons and its inputs together rather than matching them by hand. */
export type FieldSize = 'large' | 'medium' | 'small';

export type FieldOptions = {
  children?: HtmlValue;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  helperId?: string;
  helperText?: string;
  inputId?: string;
  label?: string;
  multiline?: boolean;
  required?: boolean;
  size?: FieldSize;
};

export type TextFieldOptions = {
  attributes?: Readonly<Record<string, AttributeValue>>;
  className?: string;
  disabled?: boolean;
  endAdornment?: HtmlValue;
  error?: boolean;
  helperId?: string;
  helperText?: string;
  hideLabel?: string;
  id: string;
  isRevealed?: boolean;
  label?: string;
  multiline?: boolean;
  required?: boolean;
  revealable?: boolean;
  revealLabel?: string;
  revealTooltipId?: string;
  size?: FieldSize;
  startAdornment?: HtmlValue;
  type?: string;
  value?: string;
};

export const renderField = ({ children, className, disabled, error, helperId, helperText, inputId, label, multiline, required, size = 'medium' }: FieldOptions) =>
  html`<div${attributes({ class: joinClasses('bomdisoft-field', className) })}>${label ? html`<label${attributes({ class: 'bomdisoft-field-label', 'data-disabled': disabled, 'data-error': error, 'data-required': required, for: inputId })}>${label}</label>` : ''}<div${attributes({ class: 'bomdisoft-field-box', 'data-disabled': disabled, 'data-error': error, 'data-multiline': multiline, 'data-size': size })}>${children}</div>${helperText ? html`<p${attributes({ class: 'bomdisoft-field-helper', 'data-disabled': disabled, 'data-error': error, id: helperId })}>${helperText}</p>` : ''}</div>`;

export const renderTextField = ({ attributes: extra = {}, className, disabled, endAdornment, error, helperId, helperText, hideLabel, id, isRevealed = false, label, multiline, required, revealable, revealLabel, revealTooltipId, size, startAdornment, type, value }: TextFieldOptions) => {
  const describedBy = helperText ? helperId : extra['aria-describedby'];
  const control = { ...extra, 'aria-describedby': describedBy, 'aria-invalid': error ? 'true' : extra['aria-invalid'], disabled, id, required };
  const canReveal = Boolean(revealable) && !multiline && type === 'password';
  const field = multiline ? html`<textarea${attributes({ ...control, class: 'bomdisoft-field-input bomdisoft-field-textarea' })}>${value}</textarea>` : html`<input${attributes({ ...control, class: 'bomdisoft-field-input', type: canReveal && isRevealed ? 'text' : type, value })}>`;
  const reveal = canReveal
    ? renderTooltip({
        children: html`<button${attributes({ 'aria-controls': id, 'aria-label': isRevealed ? hideLabel : revealLabel, 'aria-pressed': isRevealed ? 'true' : 'false', class: 'bomdisoft-field-reveal', 'data-testid': `${id}-reveal`, disabled, type: 'button' })}>${renderRevealGlyph(isRevealed)}</button>`,
        id: revealTooltipId ?? `${id}-reveal-tooltip`,
        title: isRevealed ? hideLabel : revealLabel,
      })
    : '';
  return renderField({
    children: html`${startAdornment ? html`<span class="bomdisoft-field-adornment">${startAdornment}</span>` : ''}${field}${reveal}${endAdornment ? html`<span class="bomdisoft-field-adornment">${endAdornment}</span>` : ''}`,
    className,
    disabled,
    error,
    helperId: helperText ? helperId : undefined,
    helperText,
    inputId: id,
    label,
    multiline,
    required,
    size,
  });
};

export const renderRevealGlyph = (isRevealed: boolean) =>
  html`<svg aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><path d="M1.6 12S5.5 5 12 5s10.4 7 10.4 7-3.9 7-10.4 7S1.6 12 1.6 12z"></path><circle cx="12" cy="12" r="3.2"></circle>${isRevealed ? html`<path d="M4 20L20 4"></path>` : ''}</svg>`;
